/**
 * Model Selector v2 - Public API
 * Main entry point for v2 model selection system
 */

// Core components
export { ModelSelectorEngine } from "./core/ModelSelectorEngine";
export type { ModelSelectorEngineOptions } from "./core/ModelSelectorEngine";

export { ModelRegistry } from "./core/ModelRegistry";
export { RecommendationEngine } from "./core/RecommendationEngine";
// Import values for local usage
import { ModelRegistry as _ModelRegistry } from "./core/ModelRegistry";
import { ModelSelectorEngine as _ModelSelectorEngine } from "./core/ModelSelectorEngine";
import { ModelSelectorV2Facade as _ModelSelectorV2Facade } from "./compat/Facade";

// Compatibility layer
export { ModelSelectorV2Facade } from "./compat/Facade";
export type { LegacyModelChoice } from "./compat/Facade";

// Feature flags
export {
  isModelSelectorV2Enabled,
  getModelSelectorV2Config,
  logFeatureFlagUsage,
  GradualRollout,
} from "./feature-flags";

// Types
export type {
  ModelInfo,
  ModelFilter,
  RecommendationContext,
  ModelRecommendation,
  ModelUsageHistory,
  RegistryHealth,
  ProviderAdapter,
  ModelSelectorEvent,
  AuditEvent,
  ModelSelectorConfig,
  RecommendationConfig,
  PricingInfo,
  Capability,
  AvailabilityStatus,
  LegacyOptions,
} from "./types/index";

// Bring types into local scope for usage in this file
import type { ModelInfo, ModelSelectorConfig } from "./types/index";

// Convenience factory function
export function createModelSelector(options: {
  models?: ModelInfo[];
  config?: Partial<ModelSelectorConfig>;
}) {
  const registry = new _ModelRegistry();

  if (options.models) {
    registry.replaceAll(options.models);
  }

  const engine = new _ModelSelectorEngine({
    registry,
    config: options.config,
  });

  return {
    engine,
    registry,
    facade: new _ModelSelectorV2Facade({ engine, registry }),
  };
}

// Version information
export const VERSION = "2.0.0-beta";
export const BUILD_DATE = new Date().toISOString();

// Note: Removed default export to fix build issues
// Use named exports instead
